import { apiClient } from './api'
import { Notification, NotificationTemplate, PaginatedResponse } from '@/types'

class NotificationService {
  // Get user notifications
  async getNotifications(params?: {
    page?: number
    limit?: number
    notification_type?: string
    is_read?: boolean
    priority?: string
  }): Promise<PaginatedResponse<Notification>> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.notification_type) queryParams.append('notification_type', params.notification_type)
    if (params?.is_read !== undefined) queryParams.append('is_read', params.is_read.toString())
    if (params?.priority) queryParams.append('priority', params.priority)

    const queryString = queryParams.toString()
    const url = queryString ? `/notifications/?${queryString}` : '/notifications/'

    return apiClient.get<PaginatedResponse<Notification>>(url)
  }

  // Get unread notifications count
  async getUnreadCount(): Promise<{ count: number }> {
    return apiClient.get<{ count: number }>('/notifications/unread-count/')
  }

  // Mark notification as read
  async markAsRead(notificationId: number): Promise<{ success: boolean; message: string }> {
    return apiClient.patch<{ success: boolean; message: string }>(
      `/notifications/${notificationId}/mark-read/`
    )
  }

  // Mark all notifications as read
  async markAllAsRead(): Promise<{ success: boolean; message: string }> {
    return apiClient.patch<{ success: boolean; message: string }>('/notifications/mark-all-read/')
  }

  // Delete notification
  async deleteNotification(notificationId: number): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(
      `/notifications/${notificationId}/`
    )
  }

  // Get notification templates
  async getTemplates(): Promise<NotificationTemplate[]> {
    return apiClient.get<NotificationTemplate[]>('/notifications/templates/')
  }

  // Create notification from template
  async createFromTemplate(
    templateName: string,
    contextData: Record<string, any>,
    saleShopId?: number
  ): Promise<Notification> {
    return apiClient.post<Notification>('/notifications/create-from-template/', {
      template_name: templateName,
      context_data: contextData,
      sale_shop_id: saleShopId,
    })
  }

  // Get notification by ID
  async getNotification(notificationId: number): Promise<Notification> {
    return apiClient.get<Notification>(`/notifications/${notificationId}/`)
  }

  // Update notification
  async updateNotification(
    notificationId: number,
    data: Partial<Notification>
  ): Promise<Notification> {
    return apiClient.patch<Notification>(`/notifications/${notificationId}/`, data)
  }

  // Get notifications by type
  async getNotificationsByType(
    type: string,
    params?: { page?: number; limit?: number; is_read?: boolean }
  ): Promise<PaginatedResponse<Notification>> {
    return this.getNotifications({ ...params, notification_type: type })
  }

  // Get high priority notifications
  async getHighPriorityNotifications(params?: {
    page?: number
    limit?: number
  }): Promise<PaginatedResponse<Notification>> {
    return this.getNotifications({ ...params, priority: 'high' })
  }

  // Get urgent notifications
  async getUrgentNotifications(params?: {
    page?: number
    limit?: number
  }): Promise<PaginatedResponse<Notification>> {
    return this.getNotifications({ ...params, priority: 'urgent' })
  }

  // Get recent notifications (last 24 hours)
  async getRecentNotifications(params?: {
    page?: number
    limit?: number
  }): Promise<PaginatedResponse<Notification>> {
    const queryParams = new URLSearchParams()

    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())

    queryParams.append('recent', 'true')

    return apiClient.get<PaginatedResponse<Notification>>(
      `/notifications/?${queryParams.toString()}`
    )
  }

  // Handle API errors
  handleError(error: any): string {
    if (error.response?.data?.message) {
      return error.response.data.message
    }
    if (error.response?.data?.errors) {
      return Array.isArray(error.response.data.errors)
        ? error.response.data.errors.join(', ')
        : error.response.data.errors
    }
    if (error.message) {
      return error.message
    }
    return 'An unexpected error occurred'
  }
}

export const notificationService = new NotificationService()
